import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";

export default function SystemConfig() {
  const [cfg, setCfg] = useState(null);
  const [days, setDays] = useState(7);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/config").then((r) => {
      setCfg(r.data);
      setDays(r.data.effectiveness_window_days || 7);
    });
  }, []);

  const save = async (e) => {
    e.preventDefault();
    const n = parseInt(days);
    if (!Number.isFinite(n) || n < 1 || n > 60) {
      toast.error("Effectiveness window must be between 1 and 60 days.");
      return;
    }
    setBusy(true);
    try {
      const res = await api.patch("/config", { effectiveness_window_days: n });
      setCfg(res.data);
      toast.success(`Effectiveness window updated to ${n} days. New training sessions will use this value.`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="config-page">
      <PageHeader eyebrow="Super Admin" title="System configuration" subtitle="Tune scoring thresholds and effectiveness windows." breadcrumbs={[{ label: "Home", to: "/" }, { label: "Settings" }]} />
      <div className="px-4 md:px-10 py-8 max-w-xl">
        <form onSubmit={save} className="bc-card p-6 space-y-4">
          <div>
            <label className="bc-label block mb-1">Effectiveness test window (days after training)</label>
            <input type="number" min="1" max="60" required value={days} onChange={(e) => setDays(e.target.value)} className="w-full border border-[#E5E5E5] rounded-sm px-3 py-2 text-sm" data-testid="window-days" />
            <div className="text-xs text-[#666666] mt-1">Default: 7 days. Sets the due date for Effectiveness assessments auto-created after each training session.</div>
          </div>
          <div className="grid grid-cols-3 gap-3 text-sm">
            <div><div className="bc-label">Pass → L2</div><div className="font-mono mt-1">≥ {cfg?.pass_threshold_l2 || 60}%</div></div>
            <div><div className="bc-label">Pass → L3</div><div className="font-mono mt-1">&gt; {cfg?.pass_threshold_l3 || 80}%</div></div>
            <div><div className="bc-label">Pass → L4 (Adv.)</div><div className="font-mono mt-1">&gt; {cfg?.pass_threshold_l4_advance || 60}%</div></div>
          </div>
          <button type="submit" disabled={busy} data-testid="save-config" className="bg-[#C41E1E] hover:bg-[#A31818] text-white px-5 py-2.5 text-sm rounded-sm font-medium inline-flex items-center gap-1.5 disabled:opacity-60">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {busy ? "Saving…" : "Save changes"}
          </button>
        </form>
      </div>
    </div>
  );
}
